import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Input } from '@/components/ui/input';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import getApiBaseUrl from '@/utils/getBaseUrl';
import { Search } from 'lucide-react';
import React, { FC, useEffect, useRef, useState } from 'react';

// Interface for the address suggestion response items
interface AddressSuggestion {
  searchType: string | string[];
  stateId: string;
  county?: string;
  fips?: string;
  title: string;
  countyId?: string;
  state?: string;
  neighborhoodName?: string;
  neighborhoodId?: string;
  neighborhoodType?: string;
  location?: string;
  id?: string; // Add ID field for property lookup
}

// Function to fetch address suggestions from the real API
const fetchAddressSuggestions = async (query: string) => {
  if (!query || query.length < 3) {
    return [];
  }

  try {
    const response = await fetch(
      `${getApiBaseUrl()}/api/v1/properties/autocomplete`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          search: query,
        }),
      },
    );

    if (!response.ok) {
      throw new Error('Network response was not ok');
    }

    const data = await response.json();
    return Array.isArray(data?.data) ? data.data : [];
  } catch (error) {
    console.error('Error fetching address suggestions:', error);
    return [];
  }
};

interface AddressAutocompleteProps {
  onSearch: (address: string, propertyId?: string) => void;
  isLoading: boolean;
  inputOnly?: boolean;
  inputDefaultValue?: string;
  isMain?: boolean;
}

const AddressAutocomplete: FC<AddressAutocompleteProps> = ({
  onSearch,
  isLoading,
  inputOnly = false,
  inputDefaultValue,
  isMain = false,
}) => {
  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState(inputDefaultValue || '');
  const [suggestions, setSuggestions] = useState<AddressSuggestion[]>([]);
  const [debouncedValue, setDebouncedValue] = useState('');
  const [isFetching, setIsFetching] = useState(false);
  const [selectedProperty, setSelectedProperty] =
    useState<AddressSuggestion | null>(null);
  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const triggerRef = useRef<HTMLDivElement>(null);
  const submitRef = useRef<HTMLButtonElement>(null);

  // Handle input change with debounce
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setInputValue(value);
    setSelectedProperty(null); // Clear selected property when input changes

    // Clear any existing timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    // Set a new timeout for debounce
    searchTimeoutRef.current = setTimeout(() => {
      setDebouncedValue(value);
    }, 600); // 600ms debounce
  };

  // Fetch suggestions when debounced value changes
  useEffect(() => {
    const getSuggestions = async () => {
      if (debouncedValue.length < 3) {
        setSuggestions([]);
        return;
      }

      setIsFetching(true);
      try {
        const results = await fetchAddressSuggestions(debouncedValue);
        setSuggestions(results);
        if (results.length > 0) {
          setOpen(true);
        }
      } catch (error) {
        console.error('Error fetching address suggestions:', error);
      } finally {
        setIsFetching(false);
      }
    };

    getSuggestions();

    // Clean up function
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [debouncedValue]);

  useEffect(() => {
    if (inputOnly && selectedProperty && selectedProperty.id) handleSubmit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputOnly, selectedProperty]);

  const handleSubmit = (e?: React.FormEvent) => {
    if (e) e.preventDefault();

    const fbq = (
      window as Window & {
        fbq?: (
          action: string,
          event: string,
          params?: Record<string, unknown>,
        ) => void;
      }
    ).fbq;

    if (fbq) {
      fbq('track', 'Search', {
        search_string: inputValue,
      });

      if (selectedProperty?.id) {
        fbq('trackCustom', 'SearchedProperty', {
          property_id: selectedProperty.id,
          property_title: selectedProperty.title,
          search_string: inputValue,
        });
      }
    }

    if (selectedProperty && selectedProperty.id) {
      // Pass both the address and the property ID to the parent component
      onSearch(inputValue, selectedProperty.id);
    } else {
      onSearch(inputValue);
    }
  };

  const handleSelect = (suggestion: AddressSuggestion) => {
    window.sessionStorage.setItem('search', JSON.stringify(suggestion));
    setInputValue(suggestion.title);
    setSelectedProperty(suggestion);
    setOpen(false);
    // Don't auto-submit on select to allow the user to review the selection
  };

  useEffect(() => {
    if (window.sessionStorage.getItem('search')) {
      const sessionSearch = JSON.parse(window.sessionStorage.getItem('search'));

      setInputValue(sessionSearch.title);

      setSelectedProperty(sessionSearch);

      if (!isMain) onSearch(sessionSearch.title, sessionSearch.id);
    }
  }, [isMain]);

  return (
    <form onSubmit={handleSubmit} className="space-y-4 w-full">
      <div className="relative flex flex-col lg:flex-row gap-2">
        <Popover open={open && suggestions.length > 0} onOpenChange={setOpen}>
          <PopoverTrigger asChild className="w-full">
            <div className="relative w-full" ref={triggerRef}>
              <Input
                id="address"
                placeholder="123 Main St, City, State"
                value={inputDefaultValue || inputValue}
                onChange={handleInputChange}
                autoComplete="off"
                className="w-full rounded-lg bg-input border-tax-borderGray focus:border-tax-brightBlue focus:ring focus:ring-tax-brightBlue/20"
                disabled={isLoading}
              />
              {isFetching && (
                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                  <div className="h-4 w-4 animate-spin rounded-full border-2 border-tax-neutralGray border-t-tax-deepBlue"></div>
                </div>
              )}
            </div>
          </PopoverTrigger>
          <PopoverContent
            className="p-0 w-[var(--radix-popover-trigger-width)] max-w-[var(--radix-popover-trigger-width)]"
            align="start"
            onOpenAutoFocus={(e) => e.preventDefault()}
          >
            <Command>
              <CommandList>
                <CommandEmpty>No addresses found</CommandEmpty>
                <CommandGroup>
                  {suggestions.map((suggestion, index) => (
                    <CommandItem
                      key={index}
                      onSelect={() => handleSelect(suggestion)}
                      className="cursor-pointer"
                    >
                      {suggestion.title}
                    </CommandItem>
                  ))}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
        {!inputOnly && (
          <Button
            type="submit"
            className="whitespace-nowrap font-medium text-primary-foreground bg-primary hover:bg-primary/90 rounded-lg flex items-center gap-2"
            disabled={isLoading || !selectedProperty}
            ref={submitRef}
          >
            <Search className="h-4 w-4" />
            {isLoading ? 'Searching...' : 'Lookup Property'}
          </Button>
        )}
      </div>
    </form>
  );
};

export default AddressAutocomplete;
